/**
 * Shared scalar contracts.
 *
 * Authority: NIMER-SCOPE-DM-001 (§4 DDL column types, §6 fact-key namespace),
 * NIMER-SCOPE-DOC-000 §3 (field identifier format).
 *
 * Wire format is **snake_case**, matching the DDL and every JSON example in the
 * baseline documents (PRD §5.7, DM-001 §2). Do not camel-case a field on the
 * way out: the field name is part of the contract.
 */
import { z } from 'zod';
/** Primary keys the API exposes externally. */
export const UuidSchema = z.uuid();
/**
 * `bigint generated always as identity` keys (answers, scope_facts, rule_firings,
 * …). Serialised as a string on the wire: these exceed 2^53 in principle and
 * `JSON.parse` would silently round them.
 */
export const IdentityIdSchema = z.string().regex(/^[1-9]\d{0,18}$/);
export const EmailSchema = z.email().max(320);
/** `timestamptz`, always serialised as an ISO-8601 instant with an offset. */
export const TimestampSchema = z.iso.datetime({ offset: true });
export const DateSchema = z.iso.date();
/** DM-001 §2.1: `text` questions cap at 200 characters. */
export const ShortTextSchema = z.string().max(200);
/** DM-001 §2.1: `textarea` — AI-interpretable free text. */
export const LongTextSchema = z.string().max(20_000);
export const NonEmptyTextSchema = z.string().trim().min(1).max(500);
/**
 * `numeric(3,2)` in the DDL and clamped to [0, 0.99] by RUL-001 §1.1.
 *
 * The evaluator (`packages/expr`) works in integer basis points so that the
 * 0.50 / 0.85 promotion thresholds are exact; this is the *wire* form, which
 * DM-001 §4 fixes as a two-decimal number. Convert at the boundary, not inside
 * the engine.
 */
const hasAtMostTwoDecimals = (value) => Number.isFinite(value) && Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;
export const ConfidenceSchema = z
    .number()
    .min(0)
    .max(0.99)
    .refine(hasAtMostTwoDecimals, { message: 'confidence has at most two decimal places' });
/**
 * A confidence contribution. Negative deltas are allowed (DM-001 §2.3, explicit
 * opt-out); the *sum* is what gets clamped, never the individual delta.
 */
export const DeltaSchema = z
    .number()
    .min(-1)
    .max(1)
    .refine(hasAtMostTwoDecimals, { message: 'delta has at most two decimal places' });
/** Content-pack version, e.g. `1.0.0` (DM-001 §2). */
export const SemVerSchema = z.string().regex(/^\d+\.\d+\.\d+$/);
/**
 * `<SECTION>.<seq>` within a domain namespace — `APP.WEB.03`, `OT.ZONE.01`
 * (DOC-000 §3). Field IDs are stable contract keys; labels may be reworded
 * freely, IDs may not.
 */
export const QuestionIdSchema = z.string().regex(/^[A-Z][A-Z0-9]*(?:\.[A-Z][A-Z0-9]*)*\.\d{2}$/);
/** A section identifier — the same shape without the trailing sequence. */
export const SectionIdSchema = z.string().regex(/^[A-Z][A-Z0-9]*(?:\.[A-Z][A-Z0-9]*)*$/);
/** Scope-fact key, e.g. `app.web.internet_accessible` (DM-001 §6). */
export const FactKeySchema = z.string().regex(/^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/);
/**
 * An option key within a question's option list, and the same key when a
 * customer's answer names it (`answer.ts`'s `AnswerOptionKeySchema`).
 *
 * Alphanumeric start, then alphanumerics and underscores. The corpus this
 * validates uses uppercase ISO country codes (`QA`, `AE`) and digit-leading
 * numeric ranges (`1_10`, `100_plus`, `2001_10000`) as option keys, and the
 * pack build (`packages/packs`), the rule corpus and the i18n sidecars all
 * reference them verbatim. An earlier `/^[a-z][a-z0-9_]*$/` rejected every one
 * of those 71 keys — on both the pack read and the answer write — so a
 * customer could not have answered those questions even if the pack loaded.
 * The character class still excludes whitespace, dots and quotes, which is
 * what the restriction is actually for.
 */
export const OptionKeySchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_]*$/);
/**
 * Idempotency key for the batched answer write (scope-9po). Without it a retry
 * after an ambiguous 5xx double-appends: latest-per-question papers over that
 * for evaluation, but the audit trail becomes a lie.
 */
export const IdempotencyKeySchema = z.string().min(16).max(200);
/**
 * `NIMER-SCOPE-ENG-<seq>` — an **internal display ref only**. It never appears
 * in a URL, an API path, or customer-facing email: sequential refs are IDOR
 * fodder and disclose engagement volume. Address engagements by UUID.
 */
export const EngagementRefSchema = z.string().regex(/^NIMER-SCOPE-ENG-\d{4,}$/);
export const JsonValueSchema = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
]));
//# sourceMappingURL=primitives.js.map